import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mic, Square, Play, Pause, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import './AudioRecorder.css';

const AudioRecorder = ({ onAudioRecorded, maxDuration = 60, initialBlob = null, initialDuration = 0 }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(initialBlob);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(initialDuration);
  
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const intervalRef = useRef(null);
  const finalDurationRef = useRef(0);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Update state when initial props change
  useEffect(() => {
    if (initialBlob !== recordedBlob) {
      setRecordedBlob(initialBlob);
    }
    if (initialDuration !== audioDuration) {
      setAudioDuration(initialDuration);
    }
  }, [initialBlob, initialDuration]);

  // Separate effect for audio element initialization
  useEffect(() => {
    if (recordedBlob && audioRef.current) {
      // Clean up existing blob URL
      if (audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }
      
      const audioUrl = URL.createObjectURL(recordedBlob);
      audioRef.current.src = audioUrl;
      audioRef.current.load(); // Force reload the audio element
      
      // Cleanup old URL when component unmounts or blob changes
      return () => {
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
      };
    }
  }, [recordedBlob]);

  // Auto-stop recording when max duration is reached
  useEffect(() => {
    if (isRecording && recordingTime >= maxDuration) {
      stopRecording();
    }
  }, [recordingTime, isRecording, maxDuration]);

  const startRecording = async () => {
    try {
      // Check if we're in a secure context (allow local network exceptions)
      const isLocalNetwork = window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1' || 
                             window.location.hostname.startsWith('192.168.') ||
                             window.location.hostname.startsWith('10.') ||
                             window.location.hostname.startsWith('172.');
      
      if (!window.isSecureContext && !isLocalNetwork) {
        toast.error('Microphone access requires HTTPS. Please use https:// instead of http://');
        return;
      }

      // Additional check for mobile browsers on local network
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobile && !window.isSecureContext && isLocalNetwork) {
        toast('⚠️ Note: Mobile microphone access over HTTP may be limited. If recording fails, please enable HTTPS.', {
          duration: 4000,
          icon: '📱'
        });
      }

      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Microphone access is not supported in this browser');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        }
      });
      
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        
        // Create audio URL for playback
        const audioUrl = URL.createObjectURL(blob);
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
        }
        
        // Use the stored final duration
        const duration = finalDurationRef.current;
        setAudioDuration(duration);
        
        // Try to get actual audio duration as verification
        const tempAudio = new Audio(audioUrl);
        tempAudio.addEventListener('loadedmetadata', () => {
          if (tempAudio.duration && isFinite(tempAudio.duration) && !isNaN(tempAudio.duration)) {
            const actualDuration = Math.round(tempAudio.duration);
            if (actualDuration > 0) {
              setAudioDuration(actualDuration);
              onAudioRecorded(blob, actualDuration);
              return;
            }
          }
          // Fallback to recorded time
          onAudioRecorded(blob, duration);
        });
        
        // Immediate callback with recorded duration
        onAudioRecorded(blob, duration);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      finalDurationRef.current = 0;
      
      // Start timer
      intervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          finalDurationRef.current = newTime; // Store the current time
          return newTime;
        });
      }, 1000);

      toast.success('Recording started!');
      
    } catch (error) {
      console.error('Error starting recording:', error);
      
      // Provide specific error messages based on error type
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow microphone permissions and try again.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotSupportedError') {
        toast.error('Microphone recording is not supported in this browser.');
      } else if (error.name === 'SecurityError') {
        toast.error('Microphone access blocked due to security settings. Please use HTTPS.');
      } else {
        toast.error(`Microphone access failed: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      toast.success('Recording stopped!');
    }
  };

  const playRecording = async () => {
    if (audioRef.current && audioRef.current.src) {
      try {
        // Reset to beginning if at end
        if (audioRef.current.ended) {
          audioRef.current.currentTime = 0;
        }
        
        await audioRef.current.play();
        setIsPlaying(true);
        
        // Add event listener for when audio ends
        const handleEnded = () => {
          setIsPlaying(false);
          audioRef.current.removeEventListener('ended', handleEnded);
        };
        audioRef.current.addEventListener('ended', handleEnded);
        
      } catch (error) {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
        toast.error('Failed to play audio recording');
      }
    } else {
      console.error('No audio source available');
      toast.error('No audio recording available to play');
    }
  };

  const pauseRecording = () => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const resetRecording = () => {
    // Clean up audio element
    if (audioRef.current) {
      if (audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current.load();
    }
    
    setRecordedBlob(null);
    setAudioDuration(0);
    setRecordingTime(0);
    setIsPlaying(false);
    finalDurationRef.current = 0;
    onAudioRecorded(null, 0);
  };

  const formatTime = (seconds) => {
    // Handle invalid values
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) {
      return '0:00';
    }
    
    const totalSeconds = Math.floor(seconds);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRecordingProgress = () => {
    return (recordingTime / maxDuration) * 100;
  };

  return (
    <div className="audio-recorder">
      <div className="recorder-container card glass">
        {/* Recording Visualization */}
        <div className="recording-visual">
          <motion.div
            className={`recording-circle ${isRecording ? 'recording' : ''}`}
            animate={isRecording ? { scale: [1, 1.1, 1] } : { scale: 1 }}
            transition={{ duration: 1, repeat: isRecording ? Infinity : 0 }}
          >
            <div className="inner-circle">
              {isRecording ? (
                <motion.div
                  className="recording-dot"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              ) : (
                <Mic size={24} />
              )}
            </div>
          </motion.div>
          
          {isRecording && (
            <div className="progress-ring">
              <svg width="120" height="120">
                <circle
                  cx="60"
                  cy="60"
                  r="55"
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.2)"
                  strokeWidth="4"
                />
                <motion.circle
                  cx="60"
                  cy="60"
                  r="55"
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={345}
                  initial={{ strokeDashoffset: 345 }}
                  animate={{ 
                    strokeDashoffset: 345 - (345 * getRecordingProgress() / 100)
                  }}
                  transition={{ duration: 0.5 }}
                />
              </svg>
            </div>
          )}
        </div>

        {/* Timer Display */}
        <div className="timer-display">
          {isRecording ? (
            <span className="recording-time">
              {formatTime(recordingTime)} / {formatTime(maxDuration)}
            </span>
          ) : recordedBlob ? (
            <span className="recorded-time">
              Duration: {formatTime(audioDuration)}
            </span>
          ) : (
            <span className="ready-text">Ready to record</span>
          )}
        </div>

        {/* Control Buttons */}
        <div className="recorder-controls">
          {!recordedBlob ? (
            // Recording Controls
            <div className="recording-controls">
              {!isRecording ? (
                <motion.button
                  className="btn btn-danger record-btn"
                  onClick={startRecording}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Mic size={20} />
                  Start Recording
                </motion.button>
              ) : (
                <motion.button
                  className="btn btn-secondary stop-btn"
                  onClick={stopRecording}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Square size={20} />
                  Stop Recording
                </motion.button>
              )}
            </div>
          ) : (
            // Playback Controls
            <div className="playback-controls">
              <motion.button
                className="btn btn-secondary"
                onClick={isPlaying ? pauseRecording : playRecording}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                {isPlaying ? 'Pause' : 'Play'}
              </motion.button>
              
              <motion.button
                className="btn btn-secondary"
                onClick={resetRecording}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <RotateCcw size={20} />
                Re-record
              </motion.button>
            </div>
          )}
        </div>

        {/* Audio Element */}
        <audio
          ref={audioRef}
          onEnded={() => setIsPlaying(false)}
          onPause={() => setIsPlaying(false)}
          style={{ display: 'none' }}
        />
      </div>

    </div>
  );
};

export default AudioRecorder;